import type { components } from "../../api/schema";
import { parseAlignmentSearch } from "../../lib/routerSearch";

export type ReviewListSearch = Pick<
  ReturnType<typeof parseAlignmentSearch>,
  "range" | "version"
>;
export type ReviewSummary = components["schemas"]["MarkerReviewSummaryItem"];
type Marker = components["schemas"]["MarkerResponse"];
type Score = components["schemas"]["AlignmentMarkerScorecard"];

export function parseReviewListSearch(
  search: Record<string, unknown>,
): ReviewListSearch {
  const { range, version } = parseAlignmentSearch(search);
  return {
    ...(range ? { range } : {}),
    ...(version ? { version } : {}),
  };
}

export interface ReviewMarkerRow {
  summary: ReviewSummary;
  revision: number | null;
  agreement: number | null;
  missedFailures: number | null;
}

export function reviewMarkerRows(
  summaries: ReviewSummary[],
  markers: Marker[],
  scores: Score[],
): ReviewMarkerRow[] {
  const revisions = new Map(
    markers.map((marker) => [marker.id, marker.current_revision.revision]),
  );
  const byMarker = new Map(scores.map((score) => [score.marker_id, score]));
  return (
    summaries
      .map((summary) => {
        const score = byMarker.get(summary.marker_id);
        return {
          summary,
          revision: revisions.get(summary.marker_id) ?? null,
          agreement: score?.agreement ?? null,
          missedFailures: score
            ? (score.confusion ?? []).reduce(
                (count, cell) =>
                  count +
                  (cell.human === "fail" &&
                  (cell.machine === "pass" || cell.machine === "warn")
                    ? cell.count
                    : 0),
                0,
              )
            : null,
        };
      })
      // ASSUMED(Unscored markers sort after known missed-failure counts
      // when needs-review counts tie.)
      .sort(
        (left, right) =>
          right.summary.needs_review_count - left.summary.needs_review_count ||
          (right.missedFailures ?? -1) - (left.missedFailures ?? -1),
      )
  );
}
